/**
 * This file configures vuejs-router for use in the application.
 *
 * This is where you implement router logic like access checks and
 * redirects based on authentication status, etc.
 *
 * The exported `router` object must be imported as part of the vue
 * setup process in main.ts
 *
 * The actual routes are defined in @see './routes.ts'. *
 *
 */

import Vue from 'vue'
import VueRouter, { RouteRecord } from 'vue-router'

// @ts-ignore (vuetify goTo doesn't export types: // https://github.com/vuetifyjs/vuetify/issues/9648)
import goTo from 'vuetify/es5/services/goto'
import { AuthUserRolesT, AUTHUSERROLE, rolesOverlapsRoleList } from '../auth/authRoles'
import { vxm } from '../state/index'
import { RequireOnlyOne } from '../utils'
import { routes } from './routes'

Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior: (to, from, savedPosition) => {
    let scrollTo: number | string | HTMLElement = 0

    if (to.hash) {
      scrollTo = to.hash
    } else if (savedPosition) {
      scrollTo = savedPosition.y
    }

    return goTo(scrollTo, {
      duration: 140,
    })
  },
})

/**
 * Internal data structure to save a redirect destination
 *
 * It is used for remembering what page a user originally requested
 * before we send him to the login page, so that we can redirect him
 * back to that original page after successful login.
 *
 * It can either be null (no redirect) OR an object, containing
 * EITHER a 'name' or a 'path' property, corresponding to a route
 * defined in routes.ts
 */
let redirectTo: RequireOnlyOne<{
  name: string; // name of a route as defined in routes.ts
  path: string; // path of a route as defined in routes.ts
}, 'name' | 'path'> | null = null

/**
 * Route access check.
 *
 * Helper function that checks if the user has access to the requested route
 * This function is used by @see router.beforeEach().
 *
 * Takes an array of vue-js router's RouteRecords along with an array of user
 * roles, e.g. the roles of the logged-in user, and checks if the user has
 * access to all the routeRecords. If any fails it returns false.
 *
 * Implementation details:
 *
 * The check works by comparing the user roles with the optional `restrictTo`
 * property of each routeRecord. The 'restrictTo' property is a custom attribute
 * that we have defined on our routes meta property (see routes.ts). If present
 * in a route record, 'restrictTo' contains an array of user roles that have
 * access to that route.
 *
 * Note: The routeRecords argument is what you get, when you do a `$route.matched`.
 * It is an array of routes, rather than a single route, because a route may be
 * nested within other routes and `$route.matched` returns all routes in the nesting
 * hierarchy down to a specific child route. See https://router.vuejs.org/guide/advanced/meta.html.
 * Because all routeRecords in the array must pass our access check, any 'restrictTo'
 * property set on a parent route effectively also applies to its children routes
 * (meaning that it is often enough to just set a `restrictTo` property on the top
 * level route).
 *
 * @param userRoles
 * @param routeRecords
 */
const userHasRouteAccess = (userRoles: AuthUserRolesT, routeRecords: RouteRecord[]) => {
  // Iterate over every routeRecord, make sure they all are accessible
  const hasAccess = routeRecords.every((record) => {
    // if the route doesn't have any restrictions return true
    if (!record.meta?.restrictTo) return true
    // ...else inspect the restrictions and check if they overlap with the
    // user roles
    return rolesOverlapsRoleList(userRoles, record.meta.restrictTo)
  })
  return hasAccess
}

/**
 * Helper function to determine the default landing page for a logged in user
 * depending on his user role
 *
 * @param userRoles
 */
const getLoggedInLandingRoute = (userRoles: AuthUserRolesT): string => {
  if (rolesOverlapsRoleList(userRoles, [AUTHUSERROLE.THERAPIST])) return 'clients'
  return 'home'
}

/**
 * This code block listens for if the user gets logged in or out. It
 * handles the routing that must occur when that happens.
 *
 * In case the user gets logged IN, it checks whether we have set a
 * 'redirectTo' and then sends him there (otherwise we just send
 * him to 'home').
 *
 * In case the user gets logged OUT we send him to the goodbye page.
 */
vxm.authUser.$watch('isLoggedIn', (isLoggedIn) => {
  if (!isLoggedIn) {
    // User just logged OUT - redirect to logged-out page
    redirectTo = null
    router.push({ name: 'goodbye' })
  } else {
    // User was just detected to have logged IN.
    // Check if we have a redirectTo
    if (redirectTo === null || redirectTo.name === 'home') {
      // No redirect, just go home:
      const landingPage = getLoggedInLandingRoute(vxm.authUser.roles)
      redirectTo = null
      router.push({ name: landingPage })
      return
    }
    // We have a redirectTo: go there and reset the redirectTo
    const nextTo = redirectTo
    redirectTo = null
    router.push(nextTo)
  }
})

/**
 * This function kicks in before each navigation (also on first page load).
 * Here we check for access and sets a redirect if necessary.
 */
// eslint-disable-next-line consistent-return
router.beforeEach((to, from, next) => {
  // Hide login and 'goodbye' pages from and users that are already logged in
  if ((to.name === 'login' || to.name === 'goodbye') && vxm.authUser.isLoggedIn) {
    redirectTo = null
    return
  }

  // Check if the user has access
  const hasAccess = userHasRouteAccess(vxm.authUser.roles, to.matched)
  if (hasAccess) {
    next()
    return
  }

  // No access:

  const path = window.location.pathname
  // No access and not logged in -> set up a redirectTo and route to login page
  if (!vxm.authUser.isLoggedIn) {
    if (path === '/' || to.name === 'home') {
      // Use a named route, because path '/' is used for several routes (see routes.ts)
      redirectTo = { name: 'home' }
    } else {
      redirectTo = { path }
    }
    next({ name: 'login' })
  }
  // no access and already logged in - do nothing (TODO: route to no access page)
  // console.log('the logged in user does not have access to', to.name)
})

export default router
